<template>
  <div class="me-header-container">
    <me-header
      v-if="ready"
      :showStartMenu="showStartMenu"
      :brand="brand"
      :user="user"
      :profileItems="profileItems"
      :navItems="navItemsWithBadges"
      :siteMap="siteMap"
      shadow
    />
  </div>
</template>

<script>
import Pusher from 'pusher-js'
import axios from 'axios'
import throttle from 'lodash/throttle'
import Vue from 'vue'
import Toolkit from '@mercadoeletronico/me-toolkit'
Vue.use(Toolkit, {
  locale: meweb?.visitor?.lang?.cultureName || 'pt-BR'
})

const iconTypes = {
  regular: 'me-icon',
  solid: 'me-icon-s',
  light: 'me-icon-l',
  thin: 'me-icon-t'
}

const PRODUCTS_ROUTES = {
  messages: '/do/SistemaMensagens.mvc'
}

export default {
  created() {
    this.load()
  },
  computed: {
    visitor() {
      const { visitor } = window.meweb || {}
      return visitor || {}
    },
    isAnonymous() {
      return this.visitor.id === "0" || (this.visitor.fromTask && this.routeParams.Hash && this.routeParams.CodSeguranca && this.routeParams.Tarefa)
    },
    ready() {
      return this.brand && this.user
    },
    version() {
      let version = 1

      if (this.visitor.tickLastAccess) {
        version = this.visitor.tickLastAccess
      }

      if (this.visitor.backOfficeUltimoAcessoTicks) {
        version = this.visitor.backOfficeUltimoAcessoTicks
      }

      if (this.visitor.ticksUpdateCacheHeader) {
        version = this.visitor.ticksUpdateCacheHeader
      }

      return `v=${version}&id=${this.visitor.id}&lang=${this.visitor.lang?.id}`
    },
    siteMapApi() {
      const pathname = window.location.pathname
      if (pathname.indexOf('/MEPDM/') != -1 || pathname.indexOf('/PDM/') != -1) {
        return '/do/api/v1/sitemap/pdm'
      } else {
        return '/do/api/v1/sitemap'
      }
    },
    showStartMenu() {
      return this.navItems.length === 0 && !this.isAnonymous;
    },
    navItemsWithBadges() {
      return this.navItems.map(item => this.mapNavItem(item, this.badges[item.linkName]))
    },
    routeParams() {
      const params = {}
      let query = new URLSearchParams(location.search)
      for(const [key, value] of query) {
        params[key] = value
      }
      return params
    }
  },
  methods: {
    async load() {
      if (this.isAnonymous) {
        this.user = {}
        return
      }

      try {
        const { data } = await axios.get(`/do/api/v2/header?${this.version}`)
        this.brand = this.mergeBrand(data.brand)
        this.user = data.user 
        this.profileItems = data.profileItems
        this.navItems = data.navItems
        this.loadSiteMap()
        //this.loadUserInfo()
      } catch (e) {
        console.log(e)
        this.user = {}
      }
    },
    async loadUserInfo() {
      const { data } = await axios.get(`/do/api/v1/users/me`)
      const { pusherKey, pusherCluster, token } = data
      this.jwt = token?.accessToken

      this.pusher = new Pusher(pusherKey, { cluster: pusherCluster })
      this.navItems.forEach(d => this.initBadge(d))
    },
    async loadSiteMap() {
      const { data } = await axios.get(`${this.siteMapApi}?${this.version}`)
      this.siteMap = data.children || []
    },
    mergeBrand({ logo, link, iconColor, background }) {
      const brand = { ...this.brand, ...{ background } }
      if (logo) {
        brand.logo = logo
      }
      if (link) {
        brand.link = link
      }
      if (iconColor) {
        brand.iconColor = iconColor
      }
      return brand
    },
    getIconName(meIcon) {
      return meIcon.split(' ').filter(val => !Object.values(iconTypes).includes(val)).join(' ')
    },
    mapNavItem(item, badge) {
      const linkName = item.linkName?.toLowerCase()
      const currentRoute = PRODUCTS_ROUTES[linkName] || ''
      const active = currentRoute === window.location.pathname

      return {
        ...item,
        active,
        icon: active ? `me-icon-s ${this.getIconName(item.icon || '')}` : item.icon,
        badge: {
          text: item.badge?.text || badge
        }
      }
    },
    initBadge(item) {
      if (item.badgeTotalUrl) {
        this.loadBadge(item)

        if (item.badgeEvent) {
          if (!this.channel) {
            this.channel = this.pusher.subscribe(`user.${this.visitor.id}`)
          }

          this.channel.bind(item.badgeEvent, () => {
            this.loadBadge(item)
          })
        }
      }
    },
    loadBadge(item) {
      if (!this.badgeLoader[item.linkName]) {
        this.badgeLoader[item.linkName] = throttle(this.setBadgesValue, 2000)
      }

      this.badgeLoader[item.linkName](item)
    },
    async setBadgesValue(item) {
      const { data } = await axios.get(item.badgeTotalUrl, { headers: { "Authorization": `Bearer ${this.jwt}` } })
      this.$set(this.badges, item.linkName, data?.total)
    },
  },
  data() {
    return {
      brand: {
        logo: '/ME/CSS/images/logos/logo_header.svg',
        link: '/Mercado.asp',
        iconColor: 'white',
        background: {
          primaryColor: "#1052E0",
          secondaryColor: "#1052E0"
        }
      },
      user: null,
      profileItems: [],
      navItems: [],
      siteMap: [],
      pusher: null,
      channel: null,
      badges: {},
      badgeLoader: {},
      jwt: null
    }
  }
}
</script>

<style lang="scss">
  :root {
    --me-white: #FFFFFF;
    --me-primary-2: #1052E0;
    --me-primary-7: #e1f1f9;
    --me-gray-0: #1f1f1f;
    --me-danger-2: #ff5252;
    --me-shadow-level-1: 0px 4px 8px rgba(0,0,0,0.08);
  }

  div.me-header-container {
    @import "@mercadoeletronico/me-toolkit/src/scss/toolkit";
    @import "node_modules/bootstrap/scss/bootstrap";
    font-family: var(--font-family-sans-serif);
    height: 65px;

    > header {
      position: fixed!important;
      border-bottom: none!important;
      width: 100%!important;
      z-index: 1002!important;
    }
  } 
</style>